import { Box } from '@zoopla-eng/zds-components/box';
import { Button, ButtonLeft } from '@zoopla-eng/zds-components/button';
import { ControlledDialog } from '@zoopla-eng/zds-components/dialog';
import {
    AlertDisabledMedium,
    AlertMedium
} from '@zoopla-eng/zds-components/icons';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import { setupLogger } from '@zoopla/logger';
import type { FC } from 'react';
import { textStyle } from './ConsentModal.styles.css';
import type { ConsentModalProps, ModalCopy } from './ConsentModal.types';
import {
    DeleteTrackedPropertyPermissionsDocument,
    DeleteTrackedPropertyPermissionsMutation,
    DeleteTrackedPropertyPermissionsMutationVariables
} from './queries/deleteTrackedPropertyPermissions.generated';
import {
    GrantTrackedPropertyAlertConsentDocument,
    GrantTrackedPropertyAlertConsentMutation,
    GrantTrackedPropertyAlertConsentMutationVariables
} from './queries/grantTrackedPropertyAlertConsent.generated';

const consentId = '3';

export const CONSENT_MODAL_TEXT: ModalCopy = {
    grant: {
        title: 'Get email updates',
        text: 'Turn on email updates about this property and the local market',
        cancelButtonText: 'Not right now',
        submitButtonText: 'Get updates'
    },
    variation_1: {
        title: 'Get updates about your home',
        text: "We'll send you the occasional email about your home and the local market.",
        cancelButtonText: 'Not right now',
        submitButtonText: 'Update me'
    },
    variation_2: {
        title: 'Want updates about your home?',
        text: "We'll send you the occasional email about your home and the local market.",
        cancelButtonText: 'No, thanks',
        submitButtonText: 'Yes, please'
    },
    variation_3: {
        title: 'Know when things change',
        text: "We'll send you an email if information about your home or the local market changes.",
        cancelButtonText: 'No, thanks',
        submitButtonText: 'Notify me'
    },
    revoke: {
        title: 'Stop email updates',
        text: 'Turn off email updates about this property and the local market',
        cancelButtonText: 'Keep updates on',
        submitButtonText: 'Stop updates'
    }
} as const;

const logger = setupLogger({ page: 'consent-modal', custodian: 'pnp' });

export const ConsentRevokeModal: FC<
    Omit<ConsentModalProps, 'relationship'>
> = ({
    isOpen,
    updateStatus,
    uprn,
    closeModal,
    hostPage,
    handleConsentClick,
    handleDismissClick
}) => {
    const { lazyFetch: deleteTrackedPropertyPermissionsMutation } =
        useLazyFetchGraphql<
            DeleteTrackedPropertyPermissionsMutation,
            DeleteTrackedPropertyPermissionsMutationVariables
        >(DeleteTrackedPropertyPermissionsDocument);

    const revokeConsent = async () => {
        if (!uprn) return;

        if (updateStatus) {
            updateStatus(uprn, 'pending');
        }

        handleConsentClick?.();

        const { errors } = await deleteTrackedPropertyPermissionsMutation({
            uprn
        });

        if (errors) {
            logger.error(
                { err: errors, hostPage },
                `untrack property email consent error`
            );
            closeModal(false);
            return;
        }

        if (updateStatus) {
            updateStatus(uprn, 'untracked');
        }
        closeModal(true);
    };

    const dismissModal = () => {
        handleDismissClick?.();

        closeModal(false);
    };

    const onDialogOpenChange = (open: boolean) => {
        if (!open) {
            handleDismissClick?.();
            closeModal(false);
        }
    };

    return (
        <ControlledDialog
            variant="medium"
            mobileVariant="bottomSheet"
            isOpen={isOpen}
            onOpenChange={onDialogOpenChange}
            label={CONSENT_MODAL_TEXT['revoke'].title}
            title={CONSENT_MODAL_TEXT['revoke'].title}
            footer={
                <Box display="flex" columnGap="S500">
                    <Button category="tertiary" onClick={dismissModal}>
                        {CONSENT_MODAL_TEXT['revoke'].cancelButtonText}
                    </Button>
                    <ButtonLeft
                        Icon={AlertDisabledMedium}
                        category="primary"
                        onClick={revokeConsent}
                    >
                        {CONSENT_MODAL_TEXT['revoke'].submitButtonText}
                    </ButtonLeft>
                </Box>
            }
        >
            <p className={`${textStyle} ${fontGroups.whisk}`}>
                {CONSENT_MODAL_TEXT['revoke'].text}
            </p>
        </ControlledDialog>
    );
};

export const ConsentModal: FC<ConsentModalProps> = ({
    isOpen,
    updateStatus,
    uprn,
    closeModal,
    hostPage,
    relationship,
    handleConsentClick,
    handleDismissClick,
    copyKey
}) => {
    const { lazyFetch: saveTrackedProperty } = useLazyFetchGraphql<
        GrantTrackedPropertyAlertConsentMutation,
        GrantTrackedPropertyAlertConsentMutationVariables
    >(GrantTrackedPropertyAlertConsentDocument);

    const copyTextKey =
        copyKey && copyKey in CONSENT_MODAL_TEXT ? copyKey : 'grant';

    const handleConsentResponse = async (userDidConsent: boolean) => {
        if (!uprn) return;

        if (userDidConsent) {
            if (updateStatus) {
                updateStatus(uprn, 'pending');
            }

            handleConsentClick?.();

            const { errors } = await saveTrackedProperty({
                trackedProperty: {
                    uprn,
                    consentId,
                    consentText: CONSENT_MODAL_TEXT[copyTextKey].text,
                    relationship:
                        relationship !== 'NONE' ? relationship : undefined
                }
            });

            if (errors) {
                logger.error(
                    { err: errors, hostPage },
                    `track property email consent error`
                );
                closeModal(false);
                return;
            }

            if (updateStatus) {
                updateStatus(uprn, 'tracked');
            }
            closeModal(true);
        } else {
            handleDismissClick?.();
            closeModal(false);
        }
    };

    const onDialogOpenChange = (open: boolean) => {
        if (!open) {
            handleDismissClick?.();
            closeModal(false);
        }
    };

    return (
        <ControlledDialog
            variant="medium"
            mobileVariant="bottomSheet"
            isOpen={isOpen}
            onOpenChange={onDialogOpenChange}
            label={CONSENT_MODAL_TEXT[copyTextKey].title}
            title={CONSENT_MODAL_TEXT[copyTextKey].title}
            footer={
                <Box display="flex" columnGap="S500">
                    <Button
                        category="tertiary"
                        onClick={() => {
                            handleConsentResponse(false);
                        }}
                    >
                        {CONSENT_MODAL_TEXT[copyTextKey].cancelButtonText}
                    </Button>
                    <ButtonLeft
                        Icon={AlertMedium}
                        category="primary"
                        onClick={() => handleConsentResponse(true)}
                    >
                        {CONSENT_MODAL_TEXT[copyTextKey].submitButtonText}
                    </ButtonLeft>
                </Box>
            }
        >
            <p className={`${textStyle} ${fontGroups.whisk}`}>
                {CONSENT_MODAL_TEXT[copyTextKey].text}
            </p>
        </ControlledDialog>
    );
};
